import React from 'react';
import { deleteVoiceShortcut, } from '@Api';
import { IAbilities, IAuth, IOrganizationStore, } from '@Store';
import { CrudEnum, IPreviewVoiceShortcut, PermissionEnum, TVoiceShortcutSortableListCol, } from 'atlas-shared';
import { tableColumnDatetimeSorter, tableColumnStringSorter, DisplayPhoneNumber, timeSince, translatePhoneNumber, } from '@Utils';
import { TFunction, } from 'i18next';
import { NavigateFunction, } from 'react-router';
import { ColumnsType, } from 'antd/lib/table/interface';
import { listAuditButton, listCloneButton, listDeleteButton, listEditButton, GetColumnSearchProps, GetColumnSearchLikeProps, GetColumnSearchEqualProps, } from '../components';
import { IWithListProps, } from '@Hocs/with.list';

export const VoiceShortcutListColumns = (t: TFunction, navigate: NavigateFunction, showDeleted: boolean, organizations: IOrganizationStore, abilities: IAbilities, auth: IAuth, buttons: Array<(row: IPreviewVoiceShortcut) => React.ReactElement> = [], filters: IWithListProps<IPreviewVoiceShortcut, TVoiceShortcutSortableListCol>['filters'], sorter: IWithListProps<IPreviewVoiceShortcut, TVoiceShortcutSortableListCol>['sorter']): ColumnsType<IPreviewVoiceShortcut> => {
  return [
    {
      title: t('ID'),
      dataIndex: 'id',
      key: 'id',
      sorter: (a: IPreviewVoiceShortcut, b: IPreviewVoiceShortcut) => a.id - b.id,
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'id')?.order || null,
      filteredValue: filters.id || null,
      ...GetColumnSearchEqualProps(t, 'id', t('ID')),
      className: 'table-cell-id',
    },
    auth.user.__admin_organization_ids.length > 1 && {
      title: t('ORGANIZATION'),
      dataIndex: 'organization_id',
      key: 'organization_id',
      render: (organization_id: IPreviewVoiceShortcut['organization_id'], voice_shortcut: IPreviewVoiceShortcut) => organizations.dict?.[organization_id]?.title || t('ORGANIZATION_NOT_FOUND', { replace: { organization_id, }, }),
      sorter: (a: IPreviewVoiceShortcut, b: IPreviewVoiceShortcut) => (organizations.dict?.[a.organization_id]?.title || '').localeCompare(organizations.dict?.[b.organization_id]?.title || ''),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'organization_id')?.order || null,
      filteredValue: filters.organization_id || null,
      ...GetColumnSearchProps(t, 'organization_id', t('ORGANIZATION'), organizations.organizations),
      className: 'table-cell-organization_id',
    },
    {
      title: t('TITLE'),
      dataIndex: 'title',
      key: 'title',
      sorter: (a: IPreviewVoiceShortcut, b: IPreviewVoiceShortcut) => tableColumnStringSorter(a.title, b.title),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'title')?.order || null,
      filteredValue: filters.title || null,
      ...GetColumnSearchLikeProps(t, 'title', t('TITLE')),
      className: 'table-cell-title',
    },
    {
      title: t('NUMBER'),
      dataIndex: 'number',
      key: 'number',
      render: (number: IPreviewVoiceShortcut['number'], voice_shortcut: IPreviewVoiceShortcut) => <DisplayPhoneNumber t={t} phonenumber={translatePhoneNumber(number)} />,
      sorter: (a: IPreviewVoiceShortcut, b: IPreviewVoiceShortcut) => tableColumnStringSorter(a.number, b.number),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'number')?.order || null,
      filteredValue: filters.number || null,
      ...GetColumnSearchLikeProps(t, 'number', t('NUMBER')),
      className: 'table-cell-number',
    },
    {
      title: t('CREATED_AT'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at: IPreviewVoiceShortcut['created_at'], voice_shortcut: IPreviewVoiceShortcut) => <div className='date-column'>{created_at ? timeSince(t, new Date(created_at), true) : ''}</div>,
      sorter: (a: IPreviewVoiceShortcut, b: IPreviewVoiceShortcut) => tableColumnDatetimeSorter(a.created_at, b.created_at),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'created_at')?.order || null,
      className: 'table-cell-created_at',
    },
    
    {
      title: '',
      dataIndex: 'operation',
      key: 'operation',
      render: (_: any, row: IPreviewVoiceShortcut) => (
        <div className={'table-operation-wrp'}>
          {buttons.map(button => button(row))}
          {abilities.can(row.organization_id, CrudEnum.Insert, PermissionEnum.VoiceShortcut, row) && listCloneButton(t, navigate, 'voice_shortcut', row)}
          {abilities.can(row.organization_id, CrudEnum.Read, PermissionEnum.Audit, row) && listAuditButton(t, navigate, 'voice_shortcut', row)}
          {abilities.can(row.organization_id, CrudEnum.Update, PermissionEnum.VoiceShortcut, row) && listEditButton(t, navigate, 'voice_shortcut', row)}
          {abilities.can(row.organization_id, CrudEnum.Delete, PermissionEnum.VoiceShortcut, row) && listDeleteButton(t, row, deleteVoiceShortcut)}
        </div>
      ),
    },
  ].filter(Boolean) as ColumnsType<IPreviewVoiceShortcut>;

};
